import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Divider,
  Stack,
  Box,
  IconButton,
  Chip,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import { BRAND, primaryButtonSx } from "../../components/auth/authTheme";

const formatCreatedAt = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const FieldLabel = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      color: BRAND.muted,
      fontWeight: 600,
      fontSize: "0.6875rem",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const AnnouncementFields = ({ data }) => (
  <Stack spacing={2.5}>
    <Box>
      <FieldLabel>Title</FieldLabel>
      <Typography
        variant="body1"
        sx={{
          color: BRAND.navy,
          fontWeight: 600,
          fontSize: "1.0625rem",
          lineHeight: 1.4,
          wordBreak: "break-word",
        }}
      >
        {data.title}
      </Typography>
    </Box>

    <Box>
      <FieldLabel>Message</FieldLabel>
      <Typography
        variant="body1"
        sx={{
          color: BRAND.text,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.6,
          fontSize: "0.9375rem",
        }}
      >
        {data.message}
      </Typography>
    </Box>

    <Box>
      <FieldLabel>Active</FieldLabel>
      <Chip
        label={data.active ? "Active" : "Inactive"}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: "0.8125rem",
          height: 28,
          borderRadius: "8px",
          bgcolor: data.active ? "rgba(22, 163, 74, 0.12)" : "rgba(107, 114, 128, 0.12)",
          color: data.active ? BRAND.success : BRAND.muted,
          "& .MuiChip-label": { px: 1.25 },
        }}
      />
    </Box>

    <Box>
      <FieldLabel>Created At</FieldLabel>
      <Typography
        variant="body1"
        sx={{
          color: BRAND.navy,
          fontWeight: 500,
          fontSize: "0.9375rem",
        }}
      >
        {formatCreatedAt(data.created_at)}
      </Typography>
    </Box>
  </Stack>
);

const ViewAnnouncementDialog = ({ show, handleClose, data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!data) return null;

  if (isMobile) {
    return (
      <Dialog
        open={show}
        onClose={handleClose}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: BRAND.bg,
            display: "flex",
            flexDirection: "column",
            height: "100dvh",
            maxHeight: "100dvh",
            borderRadius: 0,
            m: 0,
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "#fff",
            flexShrink: 0,
            borderBottom: `1px solid ${BRAND.border}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 1,
              px: 0.5,
              pr: 2,
            }}
          >
            <IconButton
              sx={{ color: BRAND.navy }}
              onClick={handleClose}
              aria-label="Close announcement view"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              sx={{
                fontSize: "1.0625rem",
                fontWeight: 700,
                color: BRAND.navy,
                lineHeight: 1.25,
              }}
            >
              View Announcement
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            px: 2,
            pt: 2,
            pb: 2,
            bgcolor: BRAND.bg,
          }}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              border: `1px solid ${BRAND.border}`,
              p: 2,
            }}
          >
            <AnnouncementFields data={data} />
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#fff",
            borderTop: `1px solid ${BRAND.border}`,
            boxShadow: "0 -4px 24px rgba(26, 43, 74, 0.08)",
            px: 2,
            pt: 1.5,
            pb: "max(16px, env(safe-area-inset-bottom))",
            width: "100%",
          }}
        >
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
            sx={{
              ...primaryButtonSx,
              minHeight: 48,
              borderRadius: "12px",
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={show} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          color: BRAND.navy,
          fontWeight: 700,
          fontSize: "1.125rem",
          pb: 1,
        }}
      >
        View Announcement
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, pb: 3 }}>
        <AnnouncementFields data={data} />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            ...primaryButtonSx,
            minWidth: 100,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAnnouncementDialog;
